import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { toast, Toaster } from "react-hot-toast";
import { Search, RefreshCw, Download } from "lucide-react";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import subDays from "date-fns/subDays";
import addDays from "date-fns/addDays";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import addMonths from "date-fns/addMonths";
import * as XLSX from "xlsx";
import { useCurrentDomain } from "../../context/DomainContext";
import {
  getLawFirms,
  getMonthlyCaseTransferReport,
} from "../../services/masterDataService";
import { selectStyles } from "../../helpers";
import { THEME } from "../../theme/themeColors";
import MonthlyAttornyTransferdetailsReport from "./MonthlyAttornyTransferdetailsReport";
import "./MonthlyAttornyTransfersummaryReport.css";

const predefinedBottomRanges = [
  { label: "Today", value: [new Date(), new Date()] },
  { label: "Yesterday", value: [addDays(new Date(), -1), addDays(new Date(), -1)] },
  { label: "This week", value: [startOfWeek(new Date()), endOfWeek(new Date())] },
  { label: "Last 7 days", value: [subDays(new Date(), 6), new Date()] },
  { label: "Last 30 days", value: [subDays(new Date(), 29), new Date()] },
  { label: "This month", value: [startOfMonth(new Date()), endOfMonth(new Date())] },
  {
    label: "Last month",
    value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))],
  },
  {
    label: "This year",
    value: [new Date(new Date().getFullYear(), 0, 1), new Date(new Date().getFullYear(), 11, 31)],
  },
  {
    label: "Last year",
    value: [
      new Date(new Date().getFullYear() - 1, 0, 1),
      new Date(new Date().getFullYear() - 1, 11, 31),
    ],
  },
];

const pageSizeOptions = [
  { value: 25, label: "25 / page" },
  { value: 50, label: "50 / page" },
  { value: 100, label: "100 / page" },
];

const MAX_BULLETS = 1;

const LawFirmOption = (props) => {
  const { data, isSelected, innerRef, innerProps } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        cursor: "pointer",
        background: isSelected ? THEME.primary : props.isFocused ? "#f3f4f6" : "#fff",
        color: isSelected ? THEME.onPrimary : "#111827",
      }}
    >
      <input type="checkbox" checked={isSelected} readOnly style={{ margin: 0 }} />
      <span style={{ fontSize: 13 }}>{data.label}</span>
    </div>
  );
};

const toYmd = (d) => {
  if (!d) return "";
  const x = new Date(d);
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${x.getFullYear()}-${mm}-${dd}`;
};

const formatDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
};

const formatMoney = (v) =>
  v == null || v === ""
    ? ""
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const joinParts = (...parts) =>
  parts
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter(Boolean)
    .join(", ");

const splitCodes = (text) =>
  String(text || "")
    .split(";")
    .map((s) => s.replace(/\r?\n/g, " ").trim())
    .filter(Boolean);

const BulletListCell = ({ text }) => {
  const items = splitCodes(text);
  if (!items.length) return <td />;
  const visible = items.slice(0, MAX_BULLETS);
  const hidden = items.slice(MAX_BULLETS);
  return (
    <td>
      <ul className="mats-bullets">
        {visible.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ul>
      {hidden.length > 0 && (
        <span className="mats-more-wrap">
          <span className="mats-more">+{hidden.length} more</span>
          <div className="mats-more-popup">
            <ul className="mats-bullets mats-bullets-all">
              {items.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        </span>
      )}
    </td>
  );
};

const MonthlyAttornyTransfersummaryReport = () => {
  const { domain } = useCurrentDomain();

  const [lawFirmOptions, setLawFirmOptions] = useState([]);
  const [selectedLawFirms, setSelectedLawFirms] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [detailsCtx, setDetailsCtx] = useState(null);

  // Detailed (all selected) view
  const [viewMode, setViewMode] = useState("summary");
  const [detailRows, setDetailRows] = useState([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(25);
  const [detailSearch, setDetailSearch] = useState("");
  const [appliedDetailSearch, setAppliedDetailSearch] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getLawFirms(domain);
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data?.data || [];
        setLawFirmOptions(
          list.map((x) => ({
            value: x.lawFirmId || x.LawFirmId,
            label: `${x.lawFirmName || x.LawFirmName || ""} (${x.lawFirmId || x.LawFirmId || ""})`,
          }))
        );
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed to load law firms");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [domain]);

  const sortedOptions = useMemo(() => {
    const selected = new Set((selectedLawFirms || []).map((c) => c.value));
    return [...lawFirmOptions].sort((a, b) => {
      const aSel = selected.has(a.value) ? 0 : 1;
      const bSel = selected.has(b.value) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      return String(a.label).localeCompare(String(b.label));
    });
  }, [lawFirmOptions, selectedLawFirms]);

  const lawFirmIdsParam = (selectedLawFirms || [])
    .map((c) => c?.value)
    .filter(Boolean)
    .join(",");

  const loadSummary = async () => {
    if (!domain) return toast.error("Domain is missing");
    if (!fromDate || !toDate) return toast.error("Date range is required");

    setLoading(true);
    try {
      const res = await getMonthlyCaseTransferReport({
        domain,
        lawFirmIds: lawFirmIdsParam,
        fromDate: toYmd(fromDate),
        toDate: toYmd(toDate),
        tabName: "Summary",
        searchText: "",
        pageNumber: 1,
        pageSize: 25,
        isExport: false,
      });
      const data = res?.summary ? res : res?.data || {};
      const list = data?.summary || [];
      setRows(list);
      if (!list.length) toast("No data");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load report");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (lawFirmId, lawFirmName) => {
    if (!fromDate || !toDate) return toast.error("Date range is required");
    if (!lawFirmId) return;
    setDetailsCtx({
      domain,
      lawFirmId,
      lawFirmName: lawFirmName || "",
      fromDate: toYmd(fromDate),
      toDate: toYmd(toDate),
    });
  };

  const handleReset = () => {
    setSelectedLawFirms([]);
    setFromDate(null);
    setToDate(null);
    setRows([]);
    setViewMode("summary");
    setDetailRows([]);
    setDetailTotal(0);
    setDetailPage(1);
    setDetailSearch("");
    setAppliedDetailSearch("");
  };

  const handleDownload = async () => {
    if (!fromDate || !toDate) return toast.error("Date range is required");
    setLoading(true);
    try {
      const res = await getMonthlyCaseTransferReport({
        domain,
        lawFirmIds: lawFirmIdsParam,
        fromDate: toYmd(fromDate),
        toDate: toYmd(toDate),
        tabName: "Summary",
        searchText: "",
        isExport: true,
      });
      const data = res?.summary ? res : res?.data || {};
      const list = data?.summary || [];
      if (!list.length) return toast.error("No data to download");
      const mapped = list.map((r) => ({
        "Law Firm Name": r.lawFirmName,
        "Law Firm Id": r.lawFirmId,
        "Bill Count": r.billCount,
        "Total Billed": r.totalBilled,
        "Total Paid": r.totalPaid,
        "Total Balance": r.totalBalance,
      }));
      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Summary");
      XLSX.writeFile(wb, "MonthlyAttorneyTransfer_Summary.xlsx");
      toast.success(`Exported ${list.length} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  // ----- All-selected details -----
  const detailTotalPages = Math.max(1, Math.ceil(detailTotal / detailPageSize));
  const safeDetailPage = Math.min(detailPage, detailTotalPages);

  const loadAllDetails = async (page, size, search = appliedDetailSearch) => {
    if (!domain) return toast.error("Domain is missing");
    if (!fromDate || !toDate) return toast.error("Date range is required");

    setDetailLoading(true);
    try {
      const res = await getMonthlyCaseTransferReport({
        domain,
        lawFirmIds: lawFirmIdsParam,
        fromDate: toYmd(fromDate),
        toDate: toYmd(toDate),
        tabName: "Details",
        searchText: search || "",
        pageNumber: page,
        pageSize: size,
        isExport: false,
      });
      const data = res?.details ? res : res?.data || {};
      setDetailRows(data?.details || []);
      setDetailTotal(data?.totalCount || 0);
      setDetailPage(page);
      setDetailPageSize(size);
      setAppliedDetailSearch(search || "");
      if (!(data?.details || []).length) toast("No data");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load details");
      setDetailRows([]);
      setDetailTotal(0);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDetailSearch = () => loadAllDetails(1, detailPageSize, detailSearch.trim());

  const mapDetailRow = (r) => ({
    "Law Firm Name": r.lawFirmName,
    "Law Firm Id": r.lawFirmId,
    "Case Id": r.caseId,
    "Case No": r.caseNo,
    "Patient Name": r.patientName,
    "Patient Address": joinParts(r.patientAddress, r.patientCity, r.patientState, r.patientZip),
    "Patient Phone": r.patientPhone,
    "Date Of Accident": formatDate(r.dateOfAccident),
    "Date Of Birth": formatDate(r.dateOfBirth),
    "Policy Holder": r.policyHolder,
    "Policy Number": r.policyNumber,
    "Claim Number": r.claimNumber,
    "Insurance Name": r.insuranceName,
    "Insurance Address": joinParts(r.insuranceAddress, r.insuranceCity, r.state, r.insuranceZip),
    "Insurance Phone": r.insurancePhone,
    "Insurance Email": r.insuranceEmail,
    Status: r.statusName,
    "Attorney Name": r.attorneyName,
    "Attorney Address": joinParts(r.attorneyAddress, r.attorneyCity, r.attorneyState, r.attorneyZip),
    "Attorney Fax": r.attorneyFax,
    Provider: r.provider,
    "Bill Number": r.billNumber,
    "Bill Date": formatDate(r.billDate),
    "Start Visit Date": formatDate(r.startVisitDate),
    "End Visit Date": formatDate(r.endVisitDate),
    "Bill Amount": r.billAmount,
    Paid: r.paid,
    Balance: r.balance,
    "Payment Date": formatDate(r.paymentDate),
    "Case Type": r.caseTypeName,
    Location: r.location,
    "Company Id": r.companyId,
    "Diagnosis Codes": r.diagnosisCodes,
    "GYB Procedure Code": r.gybProcedureCode,
    "GYB Procedure Description": r.gybProcedureCodeDescription,
    Specialty: r.specialty,
    "Denial Reason": r.denialReason,
    Verification: r.verification,
    "Is Funded": r.isFunded,
    "Funder Name": r.funderName,
    "Portfolio Name": r.portfolioName,
    "Batch Number": r.batchNumber,
    "Batch From Bill Date": formatDate(r.batchFromBillDate),
    "Batch To Bill Date": formatDate(r.batchToBillDate),
    "Funder Note": r.funderNote,
    "Transferred On": formatDate(r.transferredOn),
  });

  const handleDetailDownload = async () => {
    if (!fromDate || !toDate) return toast.error("Date range is required");
    setDetailLoading(true);
    try {
      const res = await getMonthlyCaseTransferReport({
        domain,
        lawFirmIds: lawFirmIdsParam,
        fromDate: toYmd(fromDate),
        toDate: toYmd(toDate),
        tabName: "Details",
        searchText: appliedDetailSearch || "",
        isExport: true,
      });
      const data = res?.details ? res : res?.data || {};
      const list = data?.details || [];
      if (!list.length) return toast.error("No data to download");
      if ((data.totalCount || 0) > list.length) {
        toast.error(`Export incomplete: ${list.length} of ${data.totalCount}`);
      }
      const mapped = list.map(mapDetailRow);
      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Details");
      XLSX.writeFile(wb, "MonthlyAttorneyTransfer_AllDetails.xlsx");
      toast.success(`Exported ${list.length} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setDetailLoading(false);
    }
  };

  if (detailsCtx) {
    return (
      <MonthlyAttornyTransferdetailsReport
        domain={detailsCtx.domain}
        lawFirmId={detailsCtx.lawFirmId}
        lawFirmName={detailsCtx.lawFirmName}
        fromDate={detailsCtx.fromDate}
        toDate={detailsCtx.toDate}
        onBack={() => setDetailsCtx(null)}
      />
    );
  }

  return (
    <div className="mats-page">
      <Toaster position="top-right" />

      <div className="mats-filters">
        <div className="mats-field mats-lawfirm">
          <label>Law Firm</label>
          <Select
            options={sortedOptions}
            value={selectedLawFirms}
            onChange={(opts) => setSelectedLawFirms(opts || [])}
            styles={{
              ...selectStyles,
              control: (base, state) => ({
                ...(selectStyles.control ? selectStyles.control(base, state) : base),
                minHeight: 36,
                height: "auto",
                alignSelf: "center",
              }),
              valueContainer: (base) => ({
                ...base,
                flexWrap: "wrap",
                gap: 3,
                paddingTop: 3,
                paddingBottom: 3,
                paddingLeft: 6,
                paddingRight: 6,
              }),
              multiValue: (base) => ({
                ...base,
                margin: 0,
                maxWidth: "100%",
                background: THEME.alpha(0.12),
              }),
              multiValueLabel: (base) => ({
                ...base,
                whiteSpace: "normal",
                fontSize: 11,
                lineHeight: "14px",
                padding: "2px 4px",
              }),
              multiValueRemove: (base) => ({
                ...base,
                paddingLeft: 2,
                paddingRight: 2,
              }),
              indicatorsContainer: (base) => ({
                ...base,
                alignSelf: "center",
                paddingTop: 4,
              }),
            }}
            isMulti
            isClearable
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{ Option: LawFirmOption }}
            placeholder="Select law firm(s)"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <div className="mats-field mats-date">
          <label>Transfer Date *</label>
          <DateRangePicker
            value={fromDate && toDate ? [fromDate, toDate] : null}
            onChange={(value) => {
              if (!value || value.length !== 2) {
                setFromDate(null);
                setToDate(null);
                return;
              }
              setFromDate(value[0]);
              setToDate(value[1]);
            }}
            ranges={predefinedBottomRanges}
            placeholder="Select date range"
            format="MM/dd/yyyy"
            size="sm"
            cleanable
            placement="bottomStart"
            preventOverflow
          />
        </div>

        <div className="mats-actions">
          <button
            type="button"
            className="mats-btn mats-btn-search"
            onClick={() => {
              if (viewMode === "summary") {
                loadSummary();
              } else {
                loadAllDetails(1, detailPageSize, detailSearch.trim());
              }
            }}
          >            
            <Search size={14} />
            {loading ? "Loading..." : "Search"}
          </button>
          <button type="button" className="mats-btn mats-btn-reset" onClick={handleReset} disabled={loading || detailLoading}>
            <RefreshCw size={14} />
            Reset
          </button>
          {viewMode === "summary" && (
            <button
              type="button"
              className="mats-btn mats-btn-reset"
              onClick={handleDownload}
              disabled={loading || !rows.length}
            >
              <Download size={14} />
              Excel
            </button>
          )}
        </div>
      </div>

      <div className="mats-toggle-row">
        <div className="mats-toggle">
          <button
            type="button"
            className={`mats-toggle-btn ${viewMode === "summary" ? "active" : ""}`}
            onClick={() => {
              setViewMode("summary");
              if (fromDate && toDate) {
                loadSummary();
              }
            }}    
          >
            Summary
          </button>
          <button
            type="button"
            className={`mats-toggle-btn ${viewMode === "detailed" ? "active" : ""}`}
            onClick={() => {
              setViewMode("detailed");
              if (!fromDate || !toDate) {
                return;
              }
              loadAllDetails(1, detailPageSize, detailSearch.trim() || "");
            }}
          >
            Detailed
          </button>
        </div>

        {viewMode === "detailed" && (
          <div className="mats-detail-toolbar">
            <input
              className="mats-input"
              value={detailSearch}
              onChange={(e) => setDetailSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleDetailSearch()}
              placeholder="Case # / Case Id / Bill # / Patient"
            />
            <button
              type="button"
              className="mats-btn mats-btn-search"
              onClick={handleDetailSearch}
              disabled={detailLoading}
            >
              <Search size={14} />
              Search
            </button>
            <button
              type="button"
              className="mats-btn mats-btn-reset"
              onClick={() => {
                setDetailSearch("");
                loadAllDetails(1, detailPageSize, "");
              }}
              disabled={detailLoading}
            >
              <RefreshCw size={14} />
              Reset
            </button>
            <button
              type="button"
              className="mats-btn mats-btn-reset"
              onClick={handleDetailDownload}
              disabled={detailLoading || !detailRows.length}
            >
              <Download size={14} />
              Excel
            </button>
          </div>
        )}
      </div>

      {viewMode === "summary" && (
        <div className="mats-table-wrap">
          <table className="mats-table">
            <thead>
              <tr>
                <th>Law Firm Name</th>
                <th>Law Firm Id</th>
                <th>Bill Count</th>
                <th>Total Billed</th>
                <th>Total Paid</th>
                <th>Total Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="mats-empty">
                    {loading ? "Loading..." : "Select law firm(s) and date range, then Search"}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.lawFirmId}>
                    <td>
                      {r.billCount > 0 ? (
                        <button
                          type="button"
                          className="mats-link"
                          onClick={() => openDetails(r.lawFirmId, r.lawFirmName)}
                        >
                          {r.lawFirmName}
                        </button>
                      ) : (
                        r.lawFirmName
                      )}
                    </td>
                    <td>{r.lawFirmId}</td>
                    <td>
                      {r.billCount > 0 ? (
                        <button
                          type="button"
                          className="mats-link"
                          onClick={() => openDetails(r.lawFirmId, r.lawFirmName)}
                        >
                          {r.billCount}
                        </button>
                      ) : (
                        r.billCount
                      )}
                    </td>
                    <td>{formatMoney(r.totalBilled)}</td>
                    <td>{formatMoney(r.totalPaid)}</td>
                    <td>{formatMoney(r.totalBalance)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === "detailed" && (
        <>
          <div className="mats-table-wrap">
            {detailLoading && (
              <div className="mats-loading">Loading...</div>
            )}
            <table className="mats-table">
              <thead>
                <tr>
                  <th>Law Firm Name</th>
                  <th>Law Firm Id</th>
                  <th>Case Id</th>
                  <th>Case No</th>
                  <th>Patient Name</th>
                  <th>Patient Address</th>
                  <th>Patient Phone</th>
                  <th>Date Of Accident</th>
                  <th>Date Of Birth</th>
                  <th>Policy Holder</th>
                  <th>Policy Number</th>
                  <th>Claim Number</th>
                  <th>Insurance Name</th>
                  <th>Insurance Address</th>
                  <th>Insurance Phone</th>
                  <th>Insurance Email</th>
                  <th>Status</th>
                  <th>Attorney Name</th>
                  <th>Attorney Address</th>
                  <th>Attorney Fax</th>
                  <th>Provider</th>
                  <th>Bill Number</th>
                  <th>Bill Date</th>
                  <th>Start Visit Date</th>
                  <th>End Visit Date</th>
                  <th>Bill Amount</th>
                  <th>Paid</th>
                  <th>Balance</th>
                  <th>Payment Date</th>
                  <th>Case Type</th>
                  <th>Location</th>
                  <th>Company Id</th>
                  <th>Diagnosis Codes</th>
                  <th>GYB Procedure Code</th>
                  <th>GYB Procedure Description</th>
                  <th>Specialty</th>
                  <th>Denial Reason</th>
                  <th>Verification</th>
                  <th>Is Funded</th>
                  <th>Funder Name</th>
                  <th>Portfolio Name</th>
                  <th>Batch Number</th>
                  <th>Batch From Bill Date</th>
                  <th>Batch To Bill Date</th>
                  <th>Funder Note</th>
                  <th>Transferred On</th>
                </tr>
              </thead>
              <tbody>
                {detailRows.length === 0 ? (
                  <tr>
                    <td colSpan={46} className="mats-empty">
                      {detailLoading ? "" : "Select law firms and date, then switch to Detailed"}
                    </td>
                  </tr>
                ) : (
                  detailRows.map((r, i) => (
                    <tr key={`${r.billNumber}|${r.caseId}|${i}`}>
                      <td>{r.lawFirmName}</td>
                      <td>{r.lawFirmId}</td>
                      <td>{r.caseId}</td>
                      <td>{r.caseNo}</td>
                      <td>{r.patientName}</td>
                      <td className="mats-wrap">
                        {joinParts(r.patientAddress, r.patientCity, r.patientState, r.patientZip)}
                      </td>
                      <td>{r.patientPhone}</td>
                      <td>{formatDate(r.dateOfAccident)}</td>
                      <td>{formatDate(r.dateOfBirth)}</td>
                      <td>{r.policyHolder}</td>
                      <td>{r.policyNumber}</td>
                      <td>{r.claimNumber}</td>
                      <td>{r.insuranceName}</td>
                      <td className="mats-wrap">
                        {joinParts(r.insuranceAddress, r.insuranceCity, r.state, r.insuranceZip)}
                      </td>
                      <td>{r.insurancePhone}</td>
                      <td>{r.insuranceEmail}</td>
                      <td>{r.statusName}</td>
                      <td>{r.attorneyName}</td>
                      <td className="mats-wrap">
                        {joinParts(r.attorneyAddress, r.attorneyCity, r.attorneyState, r.attorneyZip)}
                      </td>
                      <td>{r.attorneyFax}</td>
                      <td>{r.provider}</td>
                      <td>{r.billNumber}</td>
                      <td>{formatDate(r.billDate)}</td>
                      <td>{formatDate(r.startVisitDate)}</td>
                      <td>{formatDate(r.endVisitDate)}</td>
                      <td>{formatMoney(r.billAmount)}</td>
                      <td>{formatMoney(r.paid)}</td>
                      <td>{formatMoney(r.balance)}</td>
                      <td>{formatDate(r.paymentDate)}</td>
                      <td>{r.caseTypeName}</td>
                      <td>{r.location}</td>
                      <td>{r.companyId}</td>
                      <BulletListCell text={r.diagnosisCodes} />
                      <td>{r.gybProcedureCode}</td>
                      <td className="mats-wrap">{r.gybProcedureCodeDescription}</td>
                      <td>{r.specialty}</td>
                      <BulletListCell text={r.denialReason} />
                      <BulletListCell text={r.verification} />
                      <td>{r.isFunded}</td>
                      <td>{r.funderName}</td>
                      <td>{r.portfolioName}</td>
                      <td>{r.batchNumber}</td>
                      <td>{formatDate(r.batchFromBillDate)}</td>
                      <td>{formatDate(r.batchToBillDate)}</td>
                      <td className="mats-wrap">{r.funderNote}</td>
                      <td>{formatDate(r.transferredOn)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {detailTotal > 0 && (
            <div className="mats-pager">
              <Select
                options={pageSizeOptions}
                value={pageSizeOptions.find((o) => o.value === detailPageSize)}
                onChange={(opt) => loadAllDetails(1, opt.value, appliedDetailSearch)}
                styles={selectStyles}
                menuPlacement="top"
                isSearchable={false}
              />
              <span>
                {(safeDetailPage - 1) * detailPageSize + 1}-
                {Math.min(safeDetailPage * detailPageSize, detailTotal)} of {detailTotal}
              </span>
              <button
                type="button"
                className="mats-btn mats-btn-reset"
                disabled={safeDetailPage <= 1 || detailLoading}
                onClick={() => loadAllDetails(safeDetailPage - 1, detailPageSize, appliedDetailSearch)}
              >
                Prev
              </button>
              <button
                type="button"
                className="mats-btn mats-btn-reset"
                disabled={safeDetailPage >= detailTotalPages || detailLoading}
                onClick={() => loadAllDetails(safeDetailPage + 1, detailPageSize, appliedDetailSearch)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MonthlyAttornyTransfersummaryReport;